// Audio Service - Plays sounds using WAV files only (no synthesis)
// Sounds are driven exclusively by rule actions (play_sound)

import { getSound } from './sound-manager';

class AudioService {
  private enabled = true;
  private _volume = 0.5;
  private audioCache = new Map<string, HTMLAudioElement>();

  /**
   * Play a WAV file by path.
   */
  private async playWavFile(filePath: string): Promise<boolean> {
    try {
      let audio = this.audioCache.get(filePath);
      if (!audio) {
        audio = new Audio(filePath);
        this.audioCache.set(filePath, audio);
      } else {
        audio.currentTime = 0;
      }
      audio.volume = this._volume;
      await audio.play();
      return true;
    } catch (err) {
      console.warn('[AudioService] Failed to play WAV:', filePath, err);
      // Remove from cache so it retries next time
      this.audioCache.delete(filePath);
      return false;
    }
  }

  /**
   * Play a sound by its ID - WAV file only
   */
  async playSound(soundId: string): Promise<void> {
    if (!this.enabled) return;

    const soundDef = getSound(soundId);
    if (soundDef?.file) {
      const played = await this.playWavFile(soundDef.file);
      if (played) return;
    }

    console.warn('[AudioService] Sound not available:', soundId);
  }

  /**
   * Enable/disable sound playback
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if sounds are enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Set master volume (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    this._volume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Get current volume
   */
  getVolume(): number {
    return this._volume;
  }

  /**
   * Stop all currently playing sounds
   */
  stopAll(): void {
    for (const audio of this.audioCache.values()) {
      audio.pause();
      audio.currentTime = 0;
    }
  }
}

export const audioService = new AudioService();
